
const scrollingClass = 'slider-scrolling';
const evenPerPageClass = 'even-per-page';

type ItemVisibility = {
    item: HTMLElement;
    isVisible: boolean | null;
}
export default class Slider {
    container: HTMLElement;
    track: HTMLElement;
    controls: HTMLElement;
    btnPrev: HTMLButtonElement;
    btnNext: HTMLButtonElement;
    items: HTMLElement[];
    currentIndex: number;
    startX: number;
    scrollLeft: number;
    intersectionObserver: IntersectionObserver | null = null;
    childDraggables: NodeListOf<HTMLElement> | null = null;
    dots: boolean;
    dotsControls: HTMLButtonElement[] = [];
    dotsTargets: number[] = [];
    dotsContainer: HTMLElement | null = null;
    itemVisibility: ItemVisibility[] = [];
    perPage: number = 1;
    resizeObserver: ResizeObserver | null = null;
    inlineSize: number = 0;
    evenPerPage: boolean = false;
    constructor(container: HTMLElement, dots: boolean) {
        this.container = container;
        this.track = container.querySelector('[data-slider-track]')!;
        this.controls = container.querySelector('[data-controls]')!;
        this.btnPrev = container.querySelector('[data-slider-prev]')!;
        this.btnNext = container.querySelector('[data-slider-next]')!;
        this.items = [].slice.call(this.track?.querySelectorAll('li'));
        this.childDraggables = this.track?.querySelectorAll('a, img') as NodeListOf<HTMLElement>;
        this.currentIndex = 0;
        this.startX = 0;
        this.scrollLeft = 0;
        this.dots = dots;
        this.items.forEach((item, index) => {
            item.setAttribute('tabindex', '-1');
            this.itemVisibility.push({ item, isVisible: null });
        });

        if (this.track && this.items.length > 0) {
            this.init();
        }
    }

    init() {
        this.addEventListeners();
        this.syncControls();
        this.intersectionObserver = new IntersectionObserver(this.intersectionCallback, {
            root: this.track,
            rootMargin: '0px',
            threshold: [0.75, 0.99]
        });
        this.items.forEach((item) => {
            this.intersectionObserver?.observe(item);
        });
        this.inlineSize = this.track.offsetWidth;
        this.resizeObserver = new ResizeObserver(this.resizeHandler);
        this.resizeObserver.observe(this.container);
        this.childDraggables?.forEach(item => {
            item.draggable = false;
        });
        this.initializeDots();
    }

    cleanup = () => {
        this.removeEventListeners();
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.hideControls();
        this.dotsContainer?.remove();
        this.container.classList.remove(scrollingClass, evenPerPageClass);
    }

    updatePages = () => {
        const visibleItems = this.visibleItems();
        if (visibleItems.length != this.perPage) {
            this.perPage = visibleItems.length || 1;
            this.initializeDots();
            this.evenPerPage = this.perPage % 2 === 0;
            if (this.evenPerPage) {
                this.track.classList.add(evenPerPageClass);
            } else {
                this.track.classList.remove(evenPerPageClass);
            }
        }
    }

    initializeDots = () => {
        if (this.dots) {
            if (!this.dotsContainer) {
                this.dotsContainer = document.createElement('div');
                this.dotsContainer.classList.add('slider-dots');
                this.track.parentElement?.insertBefore(this.dotsContainer, this.track.nextSibling);
            }
            else {
                this.dotsContainer.innerHTML = '';
                this.dotsControls = [];
                this.dotsTargets = [];
            }
            const dotsCount = Math.ceil(this.items.length / this.perPage);
            for (let i = 0; i < dotsCount; i++) {
                const dot = document.createElement('button');
                let targetIndex = i * this.perPage;
                if (i == dotsCount - 1) {
                    targetIndex = this.items.length - 1;
                }
                const targetItem = this.items[targetIndex];
                dot.setAttribute('type', 'button');
                dot.setAttribute('aria-label', `Show item ${targetIndex + 1}`);
                dot.setAttribute('aria-current', 'false');
                dot.setAttribute('data-dot-index', `${targetIndex}`);
                dot.classList.add('slider-dot');
                dot.addEventListener('click', () => {
                    this.track.scrollTo({
                        left: this.getSlideOffset(targetItem),
                        behavior: 'smooth'
                    });
                    targetItem.focus({ preventScroll: true });
                });
                this.dotsContainer.appendChild(dot);
                this.dotsControls.push(dot);
                this.dotsTargets.push(targetIndex);
            }
            if (this.dotsControls.length == 1) {
                this.hideControls();
            }
            else {
                this.unhideControls();
                this.syncControls();
            }
        }
        else {
            if (!this.container.classList.contains(scrollingClass)) {
                this.container.classList.add(scrollingClass);
            }
        }
    }

    addEventListeners() {
        this.btnNext.addEventListener('click', this.toNextItem);
        this.btnPrev.addEventListener('click', this.toPrevItem);
        this.track.addEventListener('mousedown', this.mouseDownHandler);
        this.btnNext.addEventListener('keydown', this.keyboardHandler);
        this.btnPrev.addEventListener('keydown', this.keyboardHandler);
    }

    removeEventListeners() {
        this.btnNext.removeEventListener('click', this.toNextItem);
        this.btnPrev.removeEventListener('click', this.toPrevItem);
        this.track.removeEventListener('mousedown', this.mouseDownHandler);
        this.btnNext.removeEventListener('keydown', this.keyboardHandler);
        this.btnPrev.removeEventListener('keydown', this.keyboardHandler);
    }

    syncControls() {
        if (this.currentIndex === 0) {
            if (document.activeElement === this.btnPrev) {
                this.btnNext.focus();
            }
            setTimeout(() => {
                this.btnPrev.setAttribute('aria-hidden', 'true');
                this.btnPrev.setAttribute('inert', '');
            }, 0);
        }
        else {
            this.btnPrev.setAttribute('aria-hidden', 'false');
            this.btnPrev.removeAttribute('inert');
        }

        const finalSlideVisible = this.itemVisibility[this.items.length - 1].isVisible;
        if (finalSlideVisible) {
            if (document.activeElement === this.btnNext) {
                this.btnPrev.focus();
            }
            setTimeout(() => {
                this.btnNext.setAttribute('aria-hidden', 'true');
                this.btnNext.setAttribute('inert', '');
            }, 0);
        }
        else {
            this.btnNext.setAttribute('aria-hidden', 'false');
            this.btnNext.removeAttribute('inert');
        }

        if (this.dots) {
            let activeDot = this.dotsControls.find(dot => parseInt(dot.getAttribute('data-dot-index') || '0', 10) === this.currentIndex);
            if (!activeDot && this.dotsTargets.length > 0) {
                const closestDotTarget = this.dotsTargets?.reduce((prev, curr) => Math.abs(curr - this.currentIndex) < Math.abs(prev - this.currentIndex) ? curr : prev);
                activeDot = this.dotsControls.find(dot => parseInt(dot.getAttribute('data-dot-index') || '0', 10) === closestDotTarget);
            }
            if (finalSlideVisible) {
                activeDot = this.dotsControls[this.dotsControls.length - 1];
            }
            this.dotsControls.forEach(dot => {
                if (dot == activeDot) {
                    if (dot.getAttribute('aria-current') !== 'true') {
                        dot.classList.add('active');
                        dot.setAttribute('aria-current', 'true');
                    }
                }
                else {
                    dot.classList.remove('active');
                    dot.removeAttribute('aria-current');
                }
            });
        }

        const allNull = this.itemVisibility.every(item => item.isVisible === null);
        setTimeout(() => {
            if (!allNull) {
                const allVisible = this.itemVisibility.every(item => item.isVisible);
                if (!allVisible) {
                    this.unhideControls();
                }
                else {
                    this.hideControls();
                }
            }
        }, 0);
    }

    hideControls() {
        if (!this.controls.hasAttribute('hidden')) {
            setTimeout(() => {
                this.controls.setAttribute('hidden', '');
                this.controls.setAttribute('inert', '');
            }, 0);
        }
        if (!this.dotsContainer?.hasAttribute('hidden')) {
            setTimeout(() => {
                this.dotsContainer?.setAttribute('hidden', '');
                this.dotsContainer?.setAttribute('inert', '');
            }, 0);
        }
    }

    unhideControls() {
        if (this.controls.hasAttribute('hidden')) {
            setTimeout(() => {
                this.controls.removeAttribute('hidden');
                this.controls.removeAttribute('inert');
            }, 0);
        }
        if (this.dotsContainer?.hasAttribute('hidden')) {
            setTimeout(() => {
                this.dotsContainer?.removeAttribute('hidden');
                this.dotsContainer?.removeAttribute('inert');
            }, 0);
        }
    }

    intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        const initialVisibility = this.visibleItems().length;
        entries.forEach((entry) => {
            const itemIndex = this.items.indexOf(entry.target as HTMLElement);
            this.itemVisibility[itemIndex].isVisible = entry.intersectionRatio > 0.99;
        });
        let visibleTarget = this.currentIndex;
        const visibleItems = this.visibleItems();
        if (visibleItems.length > 0) {
            if (this.evenPerPage) {
                visibleTarget = this.items.indexOf(visibleItems[0].item);
            }
            else {
                if (this.itemVisibility[this.items.length - 1].isVisible) {
                    visibleTarget = this.items.length - 1;
                }
                else if (this.itemVisibility[0].isVisible) {
                    visibleTarget = 0;
                }
                else if (visibleItems.length == 1) {
                    visibleTarget = this.items.indexOf(visibleItems[0].item);
                }
                else {
                    let medianVisibleIndex = Math.floor(visibleItems.length / 2);
                    visibleTarget = this.items.indexOf(visibleItems[medianVisibleIndex].item);
                }
            }
        }
        if (this.currentIndex != visibleTarget) {
            this.currentIndex = visibleTarget;
        }
        if (this.dots && !this.dotsContainer) {
            this.updatePages();
        }
        if (initialVisibility == 0) {
            this.updatePages();
        }
        this.syncControls();
    }

    toNextItem = () => {
        let nextSlideIndex = this.currentIndex + this.perPage;
        if (this.items.length - this.perPage <= nextSlideIndex) {
            nextSlideIndex = this.items.length - 1;
        }
        const nextSlide = this.items[nextSlideIndex];
        const offset = this.getSlideOffset(nextSlide);
        this.track.scrollTo({
            left: offset,
            behavior: 'smooth'
        });
    }

    toPrevItem = () => {
        let prevSlideIndex = this.currentIndex - this.perPage;
        if (this.perPage > prevSlideIndex) {
            prevSlideIndex = 0;
        }
        if (this.dots && this.currentIndex + this.perPage > (this.items.length - 1)) {
            // at the end of the slider, go to previous dot index
            prevSlideIndex = this.dotsTargets[this.dotsTargets.length - 2] || 0;
        }
        const prevSlide = this.items[prevSlideIndex];
        const offset = this.getSlideOffset(prevSlide);
        this.track.scrollTo({
            left: offset,
            behavior: 'smooth'
        });
    }

    visibleItems = () => {
        return this.itemVisibility.filter(item => item.isVisible);
    }

    getSlideOffset(slide: HTMLElement): number {
        if (slide) {
            if (this.evenPerPage) {
                const gapWidth = parseFloat(getComputedStyle(this.track).gap) || 0;
                const pageWidth = (slide.offsetWidth + gapWidth) * this.perPage - gapWidth;
                return slide.offsetLeft + (pageWidth / 2) - (this.track.offsetWidth / 2);
            }
            else {
                return slide.offsetLeft + (slide.offsetWidth / 2) - (this.track.offsetWidth / 2);
            }
        }
        return 0;
    }

    mouseDownHandler = (e: MouseEvent) => {
        if (window.matchMedia("(pointer: fine)")) {
            this.startX = e.pageX - this.track.offsetLeft;
            this.scrollLeft = this.track.scrollLeft;
            this.track.addEventListener('mousemove', this.mouseMoveHandler);
            this.track.addEventListener('mouseleave', this.mouseExitHandler);
            this.track.addEventListener('mouseup', this.mouseExitHandler);
        }
    }

    mouseMoveHandler = (e: MouseEvent) => {
        let isDragging = false;
        requestAnimationFrame(() => {
            e.preventDefault();
            // don't let dragging interrupt a regular click on a slide
            const distance = Math.abs(e.pageX - this.startX) - this.track.offsetLeft;
            if (!isDragging && distance > 5) {
                isDragging = true;
            }
            if (isDragging) {
                const x = e.pageX - this.track.offsetLeft;
                this.track.classList.add('active');
                const walk = (x - this.startX) * 1.5; // accelerate drag
                this.track.scrollLeft = this.scrollLeft - walk;
            }
        });
    }

    mouseExitHandler = () => {
        this.track.classList.remove('active');
        this.track.removeEventListener('mousemove', this.mouseMoveHandler);
        this.track.removeEventListener('mouseleave', this.mouseExitHandler);
        this.track.removeEventListener('mouseup', this.mouseExitHandler);
    }

    keyboardHandler = (e: KeyboardEvent) => {
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.toNextItem();
        }
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.toPrevItem();
        }
    }

    resizeHandler = () => {
        requestAnimationFrame(() => {
            if (this.inlineSize === this.track.offsetWidth) {
                return;
            }
            else {
                this.inlineSize = this.track.offsetWidth;
            }
            this.updatePages();
        })
    }
}